import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Observable, Subscription } from 'rxjs';
import { Vehicle } from 'src/app/models/vehicle';
import { VehiclesService } from 'src/app/services/vehicles.service';

@Component({
  selector: 'app-vehicle-selector',
  templateUrl: './vehicle-selector.component.html',
  styleUrl: './vehicle-selector.component.scss',
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    CommonModule
  ]
})
export class VehicleSelectorComponent implements OnDestroy, OnChanges  {
  @Input() visible = false;
  @Input() selectedVehicles?: Vehicle[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onConfirm = new EventEmitter<Vehicle[]>();

  filter = '';
  internalVehicles: (Vehicle & {selected: boolean}) [] = [];
  subscription: Subscription = new Subscription();

  constructor(private vehicleService: VehiclesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && !this.internalVehicles.length) {
      this.loadVehicles();
    }
    if (changes['selectedVehicles']) {
      this.applySelectedFlags();
    }
  }

  private loadVehicles(): void {
    this.subscription.add(
      this.vehicleService.getAllVehicles$().subscribe((vehicles: Vehicle[]) => {
        this.internalVehicles = vehicles.map(v => ({
          ...v,
          selected: false
        }));
        this.applySelectedFlags(); // Apply any initially passed selectedVehicles
      })
    );
  }

  private applySelectedFlags(): void {
    const selectedNames = new Set(this.selectedVehicles?.map(v => v.name));
    this.internalVehicles = this.internalVehicles.map(v => ({
      ...v,
      selected: selectedNames.has(v.name)
    }));
  }

  get filteredVehicles() {
    const term = this.filter.toLowerCase();
    return this.internalVehicles.filter(v => v.name.toLowerCase().includes(term));
  }

  /** Vehicles that were attached to the restriction but have now been unchecked. */
  get removedVehicles(): (Vehicle & { selected: boolean })[] {
    const originalNames = new Set(this.selectedVehicles?.map(v => v.name));
    return this.internalVehicles.filter(v => originalNames.has(v.name) && !v.selected);
  }

  get disconnectedVehicleNames(): string {
    return this.removedVehicles.map(v => v.name).join(', ');
  }

  get noVehiclesSelected(): boolean {
    return this.internalVehicles.length > 0 && !this.internalVehicles.some(v => v.selected);
  }

  confirmSelection(): void {
    // Name is the identity key server-side, so never emit the same name twice: if the source
    // list ever contains two vehicles sharing a name, applySelectedFlags checks both and this
    // would otherwise send a duplicate the backend rejects as an already-attached vehicle.
    const selected = [
      ...new Map(this.internalVehicles.filter(v => v.selected).map(v => [v.name, v])).values()
    ];
    this.onConfirm.emit(selected);
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  cancel(): void {
    this.close();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
